import UnleashService from '@/services/unleash'
import sentry from '@/services/sentry'
import store from '@/vuex/store'
import UnleashFlagTurnOnTime from '@/constant/UnleashFlagTurnOnTime'
import PresentationApi from '@/api/presentation'

const { unleashClient } = UnleashService

const GENERATE_OPTIONS_UNLEASH_FLAGS = {
  pickAnswer: 'release-generate-options-pick-answer',
  pickImage: 'release-generate-options-pick-answer',
  typeAnswer: 'release-generate-options-short-answer',
  matchPairs: 'release-generate-answers-match-pairs',
  correctOrder: 'release-generate-options-correct-order',
  categorise: 'release-generate-answers-categorise',
  poll: 'release-generate-options-poll',
}

const _state = {
  features: {}, // Store feature toggles
  isInitialized: false, // Track whether the client has initialized
  loading: {
    initialization: false,
    contextUpdate: false,
  },
  contextUpdateCounter: 0, // Counter for contextUpdate operations
  context: {},
}

let isStartedUnleash = false

const mutations = {
  setFeatures(state) {
    const features = unleashClient.getAllToggles()
    const featureMap = features.reduce((map, feature) => {
      map[feature.name] = feature
      return map
    }, {})
    state.features = { ...featureMap }
  },
  setInitialized(state, initialized) {
    state.isInitialized = initialized
  },
  setLoading(state, { type, loading }) {
    if (type === 'contextUpdate') {
      if (loading) {
        state.contextUpdateCounter += 1
      } else {
        state.contextUpdateCounter = Math.max(0, state.contextUpdateCounter - 1)
      }

      state.loading.contextUpdate = state.contextUpdateCounter > 0
    } else {
      state.loading[type] = loading
    }
  },
  setContext(state, context) {
    state.context = {
      ...state.context,
      ...context,
    }
  },
  startUnleash() {
    if (isStartedUnleash) return
    isStartedUnleash = true
    unleashClient.start()
  },
}

const actions = {
  async initializeFeatureToggles({ commit }) {
    commit('setLoading', { type: 'initialization', loading: true })

    try {
      unleashClient.on('initialized', async () => {
        const features = unleashClient.getAllToggles()
        if (!features || !features.length) {
          await unleashClient.updateContext({
            presenterLanguage: store.state.presenterLocale || 'en',
          })
          commit('startUnleash')
        }
        commit('setInitialized', true)
        commit('setLoading', { type: 'initialization', loading: false })
      })
      unleashClient.on('ready', async () => {
        commit('setFeatures')
        commit('setLoading', { type: 'initialization', loading: false })
      })

      // on update context
      // More detail: https://docs.getunleash.io/reference/sdks/javascript-browser#available-events
      unleashClient.on('update', () => {
        commit('setFeatures')
      })
    } catch (error) {
      commit('setLoading', false)
      sentry.captureException(error)
    } finally {
      commit('setLoading', { type: 'initialization', loading: false })
    }
  },
  async updateFeatureContext({ state, commit }, context) {
    commit('setLoading', { type: 'contextUpdate', loading: true })
    commit('setContext', context)

    try {
      await unleashClient.updateContext(state.context)
    } catch (error) {
      sentry.captureException(error)
    } finally {
      commit('setLoading', { type: 'contextUpdate', loading: false })
    }
    commit('setFeatures')
  },
  async initializeUserFeatureContext({ dispatch }, { currentUser, presenterLanguage }) {
    return dispatch('updateFeatureContext', {
      userId: currentUser.id,
      email: currentUser.email,
      plan: currentUser.plan,
      presenterLanguage,
    })
  },
  async startUnleash({ commit }) {
    commit('startUnleash')
  },

  async handlePresentationLoadFlagUnleashByShareCode({ dispatch }, shareCode) {
    try {
      const respFlagInfo = await PresentationApi.getPresentationFlagInfoByShareCode(shareCode)
      store.state.presentation.createdAt = respFlagInfo.body.createdAt
      if (UnleashService) {
        UnleashService.start()
        await UnleashService.ready
      }
      await dispatch('updateFeatureContext', {
        presentationCreatorEmail: respFlagInfo.body.creator.email,
      })
      return respFlagInfo.body
    } catch (error) {
      sentry.captureException(new Error('Update Presentation Unleash Error'), {
        extra: {
          error,
        },
      })
      return error
      // do nothing
    }
  },
  async handlePresentationLoadFlagUnleash({ dispatch }, presentationId) {
    try {
      const respFlagInfo = await PresentationApi.getPresentationFlagInfo(presentationId)
      store.state.presentation.createdAt = respFlagInfo.body.createdAt
      if (UnleashService) {
        UnleashService.start()
        await UnleashService.ready
      }
      await dispatch('updateFeatureContext', {
        presentationCreatorEmail: respFlagInfo.body.creator.email,
      })
    } catch (error) {
      sentry.captureException(new Error('Update Presentation Unleash Error'), {
        extra: {
          error,
        },
      })
      // do nothing
    }
  },
}

export const comparePresentationCreatedDateWithFlag = (presentation, flag) => {
  if (!presentation || !flag || !UnleashFlagTurnOnTime[flag]) return false
  return new Date(presentation.createdAt) > UnleashFlagTurnOnTime[flag]
}

const _getters = {
  isFeatureEnabled: (state) => (featureName) => {
    return state.features[featureName] && state.features[featureName].enabled
  },
  getVariant: (state) => (featureName) => {
    return state.features[featureName] ? state.features[featureName].variant : {
      name: 'disabled',
      enabled: false,
      payload: null,
    }
  },
  isUnleashInitialized: (state) => {
    return state.isInitialized
  },
  isLoadingFeatures: (state) => {
    return state.loading.initialization || state.loading.contextUpdate
  },
  canUseNewTooltip: (_, getters) => {
    return getters.isFeatureEnabled('release-new-tooltip')
  },
  canUseXmasDecoration: (_, getters) => {
    return getters.isFeatureEnabled('kill-switch-xmas-decoration')
  },
  canUseNewEnterpriseInquiriesForm: (_, getters) => {
    return getters.isFeatureEnabled('release-enterprise-inquiries-form')
  },
  canUseCopySharedSlides: (_, getters) => {
    return getters.isFeatureEnabled('release-copy-shared-slides')
  },
  canUseTrashBin: (_, getters) => {
    return getters.isFeatureEnabled('release-trash')
  },
  canUseCorrectCropForImageReplace: (_, getters) => {
    return getters.isFeatureEnabled('release-correct-crop-for-image-replace')
  },
  canUseAutoPrefillImageSearchTabs: (_, getters) => {
    return getters.isFeatureEnabled('release-AI-auto-prefill-image-search-keyword')
  },
  canUseAIImprovingSlideContent: (_, getters) => {
    return getters.isFeatureEnabled('release-AI-improving-slide-content')
  },
  canUseNewAutocomplete: (_, getters) => {
    return getters.isFeatureEnabled('AHA-28900-autocomplete')
  },
  canUseTemplateLibraryInEditor: (_, getters) => {
    return getters.isFeatureEnabled('release-template-library-in-editor')
  },
  canUseRefactorRightNav: (_, getters) => {
    return getters.isFeatureEnabled('release-refactor-right-nav')
  },
  canUseGoogleDriveV2: (_, getters) => {
    return getters.isFeatureEnabled('release-google-drive-v2')
  },
  canUseRequestAccessPresentation: (_, getters) => {
    return getters.isFeatureEnabled('AHA-28682-request-access-presentation')
  },
  canUseGenerateOptions: (_, getters, __, rootGetters) => {
    const { currentSlideType } = rootGetters
    const slideFlag = GENERATE_OPTIONS_UNLEASH_FLAGS[currentSlideType]
    return slideFlag ? getters.isFeatureEnabled(slideFlag) : false
  },
  canUseGenerateOptionsCorrectOrder: (_, getters) => {
    return getters.isFeatureEnabled('release-generate-options-correct-order')
  },
  showConnectionStatus: (_, getters) => {
    return getters.isFeatureEnabled('connection-status')
  },
  canUseSpellchecker: (_, getters) => {
    return getters.isFeatureEnabled('release-ai-spellchecker')
  },
  canUseQuestionSuggestions: (_, getters) => {
    return getters.isFeatureEnabled('release-question-suggestions')
  },
  canUseKeywordSuggestions: (_, getters) => {
    return getters.isFeatureEnabled('release-keywords-suggestion')
  },

  isPresentationCreatedAfterTurnOnFlag: (_, __, rootState) => {
    return (flag) => {
      if (!rootState.presentation) return false
      return comparePresentationCreatedDateWithFlag(rootState.presentation, flag)
    }
  },

  canUseGoogleSlideAddonSync: (_, getters) => {
    return getters.isFeatureEnabled('AHA-29230-google-slide-add-on-sync')
  },
  canUseNewComunityTemplate: (_, getters) => {
    return getters.isFeatureEnabled('release-organising-community-templates')
  },
  canUseOnboardingRemoveSpinnerWheel: (_, getters) => {
    return getters.isFeatureEnabled('release-onboarding-remove-spinnerwheel')
  },
  canUseShareableTemplate: (_, getters) => {
    return getters.isFeatureEnabled('release-shareable-template')
  },
  canUseTemplateLibraryEditorSearch: (_, getters) => {
    return getters.isFeatureEnabled('release-template-library-editor-search')
  },
  canUseReleaseNewDiscoverTemplates: (_, getters) => {
    return getters.isFeatureEnabled('release-new-discover-templates')
  },
  canUseExportMetabase: (_, getters) => {
    return getters.isFeatureEnabled('release-export-metabase')
  },
  canUseExperimentCSATIntoTemplate: (_, getters) => {
    return getters.isFeatureEnabled('experiment-csat-into-template')
  },
  canUseThirdPartyIntegrationsInShareModal: (_, getters) => {
    return getters.isFeatureEnabled('release-promotion-3party-33784')
  },
  canUseExportRedshift: (_, getters) => {
    return getters.isFeatureEnabled('release-export-redshift')
  },
  canRemovePowerPointButton: (_, getters) => {
    return getters.isFeatureEnabled('release-remove-powerpoint-30061')
  },
  canUseGoogleSlidesPromote: (_, getters) => {
    return getters.isFeatureEnabled('release-promote-googleslides-33785')
  },
  canUseIntegrationsSection: (_, getters) => {
    return getters.isFeatureEnabled('release-promote-googleslides-33786')
  },
  canUseNewPresenterHomepage: (_, getters) => {
    return getters.isFeatureEnabled('release-new-presenter-homepage-34125')
  },
  canUseNewButtons: (_, getters) => {
    return getters.isFeatureEnabled('release-consistent-buttons')
  },
  canUseNewColors: (_, getters) => {
    return getters.isFeatureEnabled('release-ahaslides-color-palette')
  },
  canUseMovePresentations: (_, getters) => {
    return getters.isFeatureEnabled('move-presentation')
  },
  isCacheCanvasBlocks: (_, getters) => {
    return getters.isFeatureEnabled('cache-canvas-blocks')
  },
  canUseNewSlideIcon: (_, getters) => {
    return getters.isFeatureEnabled('release-new-slide-icon')
  },
  canUseSlideFixSelectModeActionButtons: (_, getters) => {
    return getters.isFeatureEnabled('release-gridview-selectmode-actionbuttons-fix')
  },
}

export const isFeatureEnabled = (rootGetters, featureName) => {
  return rootGetters['featureToggles/isFeatureEnabled'](featureName)
}

export const getVariant = (rootGetters, featureName) => {
  return rootGetters['featureToggles/getVariant'](featureName)
}

export const isPresentationCreatedAfterTurnOnFlag = (rootGetters, flag) => {
  return rootGetters['featureToggles/isPresentationCreatedAfterTurnOnFlag'](flag)
}

export const isPresentationResetAfterTurnOnFlag = (presentation, flag) => {
  if (!presentation ||
    !presentation.resetTimeStamp ||
    !flag ||
    !UnleashFlagTurnOnTime[flag]
  ) {
    return false
  }
  // resetTimeStamp is a string, so need to convert it to number before parsing to Date object
  return new Date(Number(presentation.resetTimeStamp)) > UnleashFlagTurnOnTime[flag]
}

export default {
  namespaced: true,
  state: _state,
  mutations,
  actions,
  getters: _getters,
}
